import React from 'react';
import { Box, Typography, Select, MenuItem, Tooltip } from '@mui/material';
import { WriteButton, WriteIconButton } from '../../ui';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { itemsForMenu, labelForKey, isKnownMenuKey, MenuKind } from '../../../config/benefitsMenuCatalog';

interface Props {
  label: string;
  description?: string;
  value: unknown;
  onChange: (v: string[]) => void;
  menuKind?: MenuKind;
}

const MenuListField: React.FC<Props> = ({ label, description, value, onChange, menuKind = 'hamburger' }) => {
  const items: string[] = Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
  const catalog = itemsForMenu(menuKind);
  const selected = new Set(items.filter(Boolean));

  const setItem = (idx: number, key: string) => {
    const next = [...items];
    next[idx] = key;
    onChange(next);
  };
  const removeItem = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const addItem = () => onChange([...items, '']);
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  // Options for a given row: catalog items not used elsewhere, plus the row's
  // own current value (even if custom/unknown) so nothing is dropped.
  const optionsFor = (cur: string): { key: string; label: string }[] => {
    const opts = catalog
      .filter((c) => c.key === cur || !selected.has(c.key))
      .map((c) => ({ key: c.key, label: c.label }));
    if (cur && !catalog.some((c) => c.key === cur)) {
      opts.unshift({ key: cur, label: isKnownMenuKey(cur) ? labelForKey(cur) : `${cur} (custom)` });
    }
    return opts;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#141417', fontFamily: 'Manrope, sans-serif' }}>{label}</Typography>
          {description && <Typography sx={{ fontSize: 11, color: '#6F6F6F' }}>{description}</Typography>}
        </Box>
        <WriteButton size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addItem}
          sx={{ textTransform: 'none', fontSize: 12, color: '#0078B3' }}>
          Add
        </WriteButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {items.map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: '#A1A1A1', minWidth: 20, flexShrink: 0 }}>{idx + 1}.</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Select
                size="small" fullWidth displayEmpty value={item || ''}
                onChange={(e) => setItem(idx, e.target.value)}
                sx={{ fontSize: 13, borderRadius: '6px' }}
              >
                <MenuItem value="" disabled><em>Select an item…</em></MenuItem>
                {optionsFor(item).map((o) => (
                  <MenuItem key={o.key} value={o.key} sx={{ fontSize: 13 }}>
                    {o.label}
                    <Typography component="span" sx={{ ml: 1, fontFamily: 'monospace', fontSize: 11, color: '#A1A1A1' }}>{o.key}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Tooltip title="Move up"><span><WriteIconButton size="small" sx={{ flexShrink: 0 }} disabled={idx === 0} onClick={() => move(idx, -1)}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></WriteIconButton></span></Tooltip>
            <Tooltip title="Move down"><span><WriteIconButton size="small" sx={{ flexShrink: 0 }} disabled={idx === items.length - 1} onClick={() => move(idx, 1)}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></WriteIconButton></span></Tooltip>
            <WriteIconButton size="small" onClick={() => removeItem(idx)} sx={{ color: '#E53935', flexShrink: 0 }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></WriteIconButton>
          </Box>
        ))}
        {items.length === 0 && (
          <Typography sx={{ fontSize: 12, color: '#A1A1A1', fontStyle: 'italic', py: 0.5 }}>No items yet — click Add.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default MenuListField;
